import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Heart, Play as PlayIcon } from 'lucide-react';
import { fetchFeed } from '~/api/feed';
import { listGenres } from '~/api/matches';
import { buildHead } from '~/seo/site';
import { WaveformPlayer } from '~/ui/WaveformPlayer';
import { Badge } from '~/ui/components/badge';
import { Card, CardContent, CardHeader } from '~/ui/components/card';

type FeedSearch = { genre?: string };

export const Route = createFileRoute('/feed')({
  head: () =>
    buildHead({
      title: 'Discover Beats',
      description:
        'Latest submissions from active beat battles. Filter by genre. Listen before you vote.',
      path: '/feed',
    }),
  component: Feed,
  validateSearch: (search: Record<string, unknown>): FeedSearch => ({
    genre: typeof search.genre === 'string' ? search.genre : undefined,
  }),
});

function Feed() {
  // Genre filter is reflected in the URL so it survives reload + can be
  // shared. `undefined` means "all genres".
  const { genre } = Route.useSearch();
  const navigate = useNavigate({ from: '/feed' });

  // System genres are the curated catalogue we run battles on. User-proposed
  // genres are excluded from the filter to keep the chip row from getting
  // unwieldy and to avoid empty-result chips.
  const { data: genres } = useQuery({
    queryKey: ['genres', 'system'],
    queryFn: () => listGenres({ kind: 'system' }),
    staleTime: 5 * 60_000,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['feed', { genre }],
    queryFn: () => fetchFeed({ limit: 20, genre }),
  });

  const setGenre = (next: string | undefined) => {
    navigate({ search: next ? { genre: next } : {}, replace: true });
  };

  return (
    <section className="mx-auto max-w-3xl px-4 py-10 space-y-6">
      <header>
        <h2 className="text-3xl font-bold">Discover</h2>
        <p className="text-muted-foreground">
          Match winners only - the beats that took 1st in their room. Filter by genre below.
        </p>
      </header>

      <nav
        aria-label="Filter feed by genre"
        className="flex flex-wrap gap-2 pb-1 font-mono text-[11px] uppercase tracking-wider"
      >
        <FilterChip active={!genre} onClick={() => setGenre(undefined)} label="all" />
        {genres?.map((g) => (
          <FilterChip
            key={g.slug}
            active={genre === g.slug}
            onClick={() => setGenre(g.slug)}
            label={g.name.toLowerCase()}
          />
        ))}
      </nav>

      {isLoading && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground">
          Loading...
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm">
          Couldn't load the feed. Is the API running at{' '}
          {import.meta.env.VITE_API_URL ?? '(default)'}?
        </div>
      )}

      {data && data.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-8 space-y-4">
          <div>
            <div className="text-lg font-medium">
              {genre ? (
                <>
                  No submissions yet in <span className="font-mono">{genre}</span>
                </>
              ) : (
                'No submissions yet'
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Submit one and it'll be the first thing here.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Link
              to="/play"
              className="rounded-md border border-primary/40 bg-primary/5 hover:bg-primary/10 p-4 text-sm space-y-1"
            >
              <div className="font-medium">Start a match</div>
              <div className="text-xs text-muted-foreground">
                Make a beat, audience votes blind, your track lands here.
              </div>
            </Link>
            <Link
              to="/lobbies"
              className="rounded-md border border-border hover:border-primary/40 p-4 text-sm space-y-1"
            >
              <div className="font-medium">Join a live lobby</div>
              <div className="text-xs text-muted-foreground">
                Drop into an open match in any genre.
              </div>
            </Link>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {data?.map((item) => (
          <Card key={item.id}>
            <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{item.title ?? 'Untitled'}</span>
                  <Badge variant="secondary">{item.genre.name}</Badge>
                  {item.finalRank === 1 && <Badge>winner</Badge>}
                  {item.arPicks && item.arPicks.count > 0 && (
                    <Badge
                      variant="outline"
                      className="border-violet-400/40 bg-violet-400/10 text-violet-300"
                      title={`${item.arPicks.count} A&R ${item.arPicks.count === 1 ? 'pick' : 'picks'}${item.arPicks.cosigned ? ' · cosigned' : ''}`}
                    >
                      {item.arPicks.cosigned ? 'A&R Co-sign' : "A&R's Choice"}
                    </Badge>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  <Link
                    to="/$username"
                    params={{ username: item.producer.handle }}
                    className="hover:text-primary hover:underline"
                  >
                    @{item.producer.handle}
                  </Link>
                  {item.description ? ` · ${item.description}` : ''}
                </div>
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-3 shrink-0 pt-1">
                <span className="inline-flex items-center gap-1">
                  <PlayIcon className="size-3" /> {item.plays}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Heart className="size-3" /> {item.likes}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <WaveformPlayer src={item.audioUrl} />
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'true' : undefined}
      className={`shrink-0 rounded-full border px-3 py-1 transition-colors ${
        active
          ? 'border-primary bg-primary/15 text-foreground'
          : 'border-border bg-muted/30 text-muted-foreground hover:border-foreground/40 hover:text-foreground'
      }`}
    >
      {label}
    </button>
  );
}
